'use client';

import { useField } from 'formik';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';

import { PROVINCE_CODES } from '@commons/i18n/en/locations';

import Select, { type Option, type SelectProps } from './';

// Type definition for the ProvinceSelect component properties.

type Props = Omit<SelectProps, 'options'> & {
  useLabelAsValue?: boolean; // New prop to control value type
  variant?: 'primary' | 'secondary';
};

/**
 * ProvinceSelect component.
 *
 * Renders a dropdown to select provinces in Canada.
 */

export const ProvinceSelect = ({
  useLabelAsValue = false,
  variant = 'primary',
  ...props
}: Props) => {
  const t = useTranslations('locations.provinces');

  const options: Option[] = useMemo(() => {
    return PROVINCE_CODES.map((code) => ({
      label: t(code),
      value: useLabelAsValue ? t(code) : code,
    }));
  }, [t, useLabelAsValue]);

  return <Select {...props} variant={variant} options={options} searchable />;
};

/**
 * FormikProvinceSelect component.
 *
 * A Formik-compatible wrapper for `ProvinceSelect`, allowing integration of form state
 * management and error handling with Formik context.
 */

export const FormikProvinceSelect = ({ name, ...props }: Props) => {
  const [field, meta, helpers] = useField(name);

  const { touched, error } = meta;
  const { setValue } = helpers;

  const isError = touched && error !== undefined;

  return (
    <div className="notranslate" translate="no">
      <ProvinceSelect onValueChange={setValue} error={isError} {...field} {...props} />
    </div>
  );
};

export default ProvinceSelect;
